import { snakeCaseify } from '../../../utils'

export const EditMenuItem =
  ({ ChefService, RestService, UIService, pResponseMenuItem }) =>
  async (menuItemId) => {
    const menuItem = await RestService.get(
      `/api/admin/menu_items/${menuItemId}`,
    )
    ChefService.setEditMenuItem(pResponseMenuItem(menuItem))
    UIService.EditMenuItem.show()
  }

export const LoadMenuItem =
  ({ RestService, HandleError, pResponseMenuItem }) =>
  async (menuItemId) => {
    try {
      const menuItem = await RestService.get(
        `/api/admin/menu_items/${menuItemId}`,
      )

      return pResponseMenuItem(menuItem)
    } catch (error) {
      HandleError({ error })

      return false
    }
  }

export const CopyMenuItem =
  ({ RestService, UIService, HandleError }) =>
  async (menuItemId, chefId) => {
    try {
      await RestService.post(`/api/admin/menu_items/${menuItemId}/copy`, {
        chef_id: chefId,
      })
      UIService.EditMenuItem.hideCopyMenuItem()
      UIService.FlashMessage.displaySuccessMessage(
        'Successfully copied menu item.',
      )

      return true
    } catch (error) {
      HandleError({ error })

      return false
    }
  }

export const EditChildMenuItem =
  ({ ChefService, RestService, UIService, pResponseMenuItem }) =>
  async (menuItemId, parentMenuItemId = undefined) => {
    if (parentMenuItemId) {
      const parentMenuItem = await RestService.get(
        `/api/admin/menu_items/${parentMenuItemId}`,
      )
      ChefService.setEditMenuItem(pResponseMenuItem(parentMenuItem))
    }
    const menuItem = await RestService.get(
      `/api/admin/menu_items/${menuItemId}`,
    )
    ChefService.setEditChildMenuItem(pResponseMenuItem(menuItem))
    UIService.EditMenuItem.close()
    UIService.EditChildMenuItem.show()
  }

export const NewMenuItem =
  ({ ChefService, UIService, pDefaultMenuFields }) =>
  () => {
    const { chef, editMenuItem } = ChefService.getState()
    if (editMenuItem.id) {
      ChefService.clearEditMenuItem()
    }

    if (chef) {
      ChefService.setEditMenuItem(pDefaultMenuFields(chef))
      UIService.EditMenuItem.show()
    }
  }

export const DraftComposedAlternativeMenuItem =
  ({ ChefService, UIService, pDefaultComposedAltItemFields }) =>
  (vendorDescription, customizableItemId) => {
    const { chef, editMenuItem } = ChefService.getState()

    if (editMenuItem.id) {
      ChefService.clearEditMenuItem()
      UIService.EditMenuItem.close()
    }
    if (chef) {
      UIService.InfoModal.dismissInfoModal()
      ChefService.setEditMenuItem(
        pDefaultComposedAltItemFields(
          chef,
          vendorDescription,
          customizableItemId,
        ),
      )
      UIService.EditMenuItem.show()
    }
  }

export const UpsertComposedMenuItem =
  ({
    RestService,
    UIService,
    pGetRelatedChefMenuItemIds,
    pRequestComposedAltChefMenuItem,
    pResponseChefMenuItem,
    HandleError,
  }) =>
  async (composedItemId, chefId, customItemId, refetchCustomItem = false) => {
    try {
      const req = pRequestComposedAltChefMenuItem(
        chefId,
        composedItemId,
        customItemId,
      )
      let chefMenuItem = await RestService.put(
        'group-orders/chef-menu-items/composed',
        req,
      )
      if (refetchCustomItem) {
        chefMenuItem = await RestService.get(
          `group-orders/chef-menu-items/${customItemId}`,
        )
      }
      if (chefMenuItem) {
        UIService.InfoModal.dismissInfoModal()
        let items = []
        const ids = pGetRelatedChefMenuItemIds(chefMenuItem)
        if (ids.length > 0) {
          items = await RestService.get('/api/admin/menu_items', { ids: ids })
        }

        return pResponseChefMenuItem(chefMenuItem, items)
      }
    } catch (error) {
      HandleError({ error })
    }
  }

export const NewChildMenuItem =
  ({ ChefService, UIService, pDefaultChildMenuFields }) =>
  ({ parentMenuItemId, parentMenuItemName, mealType }, parentMenuItem) => {
    const { chef } = ChefService.getState()
    UIService.EditMenuItem.close()
    ChefService.setEditMenuItem(parentMenuItem)
    ChefService.setEditChildMenuItem(
      pDefaultChildMenuFields({
        chef,
        parentMenuItemId,
        parentMenuItemName,
        mealType,
      }),
    )
    UIService.EditChildMenuItem.show()
  }

export const CloseChildMenuItemModal =
  ({ ChefService, UIService, pStateToReduxMenuItem }) =>
  (child) => {
    UIService.EditChildMenuItem.close()
    UIService.EditMenuItem.show()

    const { editMenuItem } = ChefService.getState()
    const { childMenuItems } = editMenuItem
    const current = childMenuItems.find((c) => c.id === child.id)
    if (current) {
      const index = childMenuItems.indexOf(current)
      ChefService.setEditMenuItem({
        ...editMenuItem,
        childMenuItems: [
          ...childMenuItems.slice(0, index),
          {
            ...current,
            ...pStateToReduxMenuItem(child),
          },
          ...childMenuItems.slice(index + 1),
        ],
      })
    } else if (child.id) {
      // only include if the menu item has valid id
      ChefService.setEditMenuItem({
        ...editMenuItem,
        childMenuItems: [...childMenuItems, pStateToReduxMenuItem(child)],
      })
    }
  }

export const UpdateMenuItem =
  ({ ChefService, pStateToReduxMenuItem }) =>
  (menuItem, menuItemSettings = {}) => {
    ChefService.setEditMenuItem(
      pStateToReduxMenuItem(menuItem),
      menuItemSettings,
    )
  }

export const UpdateChildMenuItem =
  ({ ChefService, pStateToReduxMenuItem }) =>
  (menuItem, menuItemSettings = {}) => {
    ChefService.setEditChildMenuItem(
      pStateToReduxMenuItem(menuItem),
      menuItemSettings,
    )
  }

export const LoadChefMenuItems =
  ({
    ChefService,
    RestService,
    pResponseMenuItems,
    pSerializeLoadMenuItemsParams,
  }) =>
  async (params) => {
    const queryParams = pSerializeLoadMenuItemsParams({
      ...params,
      serializer: 'chef_tab',
    })

    const response = await RestService.get(
      '/api/admin/menu_items',
      queryParams,
      {
        timeout: 35000,
      },
    )
    const menuItems = pResponseMenuItems(response)

    ChefService.setChefMenuItems(menuItems)
  }

export const LoadAllChefMenuItemsIntoRedux =
  ({
    ChefService,
    RestService,
    pResponseMenuItems,
    pSerializeLoadMenuItemsParams,
    HandleError,
  }) =>
  async (params) => {
    const lastFetchedAt = ChefService.getLastFetchedAllMenuItemsAt()
    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000

    const shouldRefetch = !lastFetchedAt || now - lastFetchedAt > fiveMinutes
    if (!shouldRefetch) {
      return
    }

    try {
      const mainMenuItems = []
      const sideMenuItems = []
      const otherMenuItems = []
      const resultsPerPage = 50
      let page = 1
      let finished = false
      while (!finished) {
        const queryParams = pSerializeLoadMenuItemsParams({
          ...params,
          page,
          resultsPerPage,
          serializer: 'chef_tab',
        })
        const response = await RestService.get(
          '/api/admin/menu_items',
          queryParams,
          {
            timeout: 35000,
          },
        )

        const {
          mainMenuItems: pageMain,
          sideMenuItems: pageSides,
          otherMenuItems: pageOther,
        } = pResponseMenuItems(response)

        mainMenuItems.push(...pageMain)
        sideMenuItems.push(...pageSides)
        otherMenuItems.push(...pageOther)
        const pageCount = pageMain.length + pageSides.length + pageOther.length
        finished = pageCount < resultsPerPage
        page += 1
      }

      ChefService.setAllChefMenuItems({
        mainMenuItems,
        sideMenuItems,
        otherMenuItems,
      })
    } catch (error) {
      HandleError({ error })
    }
  }

export const LoadChefMenuItemsForAudits =
  ({
    RestService,
    pResponseAuditItems,
    pSerializeLoadMenuItemsParams,
    HandleError,
  }) =>
  async (params) => {
    const queryParams = pSerializeLoadMenuItemsParams({
      ...params,
      serializer: 'chef_items_audit_tab',
    })

    try {
      const response = await RestService.get(
        '/api/admin/menu_items',
        queryParams,
        {
          timeout: 35000,
        },
      )
      const menuItems = pResponseAuditItems(response)

      return menuItems
    } catch (error) {
      HandleError({ error })

      return []
    }
  }

export const LoadGroupOrderAddOnItems =
  ({ RestService, pResponseMenuItems }) =>
  async (filters) => {
    filters = snakeCaseify(filters)
    try {
      const response = await RestService.get(
        '/api/admin/menu_items',
        { page: 1, results_per_page: 100, ...filters },
        { timeout: 35000 },
      )

      return pResponseMenuItems(response)
    } catch (e) {
      return []
    }
  }

export const LoadMenuItemRatings =
  ({ RestService }) =>
  async (menuItemIds) => {
    const ratings = await RestService.put('group-orders/menu-item-ratings', {
      menuItemIds,
    })

    return ratings
  }

export const LoadPopUpMenuItems =
  ({ RestService, pResponseMenuItems }) =>
  async (chefId) => {
    const params = {
      parent_items: true,
      include_all: true,
      is_approved: true,
      chef_ids: [chefId],
    }

    const response = await RestService.get('/api/admin/menu_items', params, {
      timeout: 35000,
    })

    return pResponseMenuItems(response)
  }

export const SaveMenuItem =
  ({
    ChefService,
    RestService,
    UIService,
    pRequestComposedAltChefMenuItem,
    pRequestUpdateMarketplaceAddOn,
    pRequestUpdateMenuItem,
    pRequestUpdateItemImages,
    pResponseMenuItem,
    pResponseMenuItems,
    pSerializeLoadMenuItemsParams,
    CloseChildMenuItemModal,
    EditMenuItem,
    HandleError,
    pStateToReduxMenuItem,
  }) =>
  async (data, isCopy, needResponsePresenter) => {
    if (
      !data.isSubMenuItem &&
      Array.isArray(data.tagsDietaryPreference) &&
      data.tagsDietaryPreference.length === 0
    ) {
      UIService.FlashMessage.displayFailureMessage(
        'Please select an Allergen/Dietary preference for this item.',
      )

      return
    }

    try {
      UIService.Errors.clear()
      const req = pRequestUpdateMenuItem(data)
      const headers = { 'Content-Type': 'multipart/form-data' }

      // create menu item
      let menuItem = undefined
      if (data.id) {
        menuItem = await RestService.put(
          `/api/admin/menu_items/${data.id}`,
          req,
          headers,
        )
      } else {
        menuItem = await RestService.post('/api/admin/menu_items', req, headers)
        // Create Chef Menu Item
        if (data.customizableItemId) {
          await RestService.put(
            'group-orders/chef-menu-items/composed',
            pRequestComposedAltChefMenuItem(
              data.chefId,
              menuItem.id,
              data.customizableItemId,
            ),
          )
        }
      }

      // create item's images
      const imageUpdateReq = pRequestUpdateItemImages(data)
      if (menuItem && menuItem.id && imageUpdateReq) {
        menuItem = await RestService.put(
          `/api/admin/menu_items/${menuItem.id}`,
          imageUpdateReq,
          headers,
        )
      }

      // create marketplace add on
      const marketplaceAddOnReq = pRequestUpdateMarketplaceAddOn({
        menuItemData: data,
        menuItemId: menuItem.id,
      })
      if (marketplaceAddOnReq && !marketplaceAddOnReq.destroy) {
        await RestService.post(
          '/api/admin/marketplace_add_on',
          marketplaceAddOnReq,
          headers,
        )
      } else if (marketplaceAddOnReq && marketplaceAddOnReq.destroy) {
        await RestService.delete(
          `/api/admin/marketplace_add_on/${marketplaceAddOnReq.id}`,
        )
      }

      // child items have no closing actions
      if (isCopy && data.isSubMenuItem) {
        return
      }

      if (data.isSubMenuItem) {
        CloseChildMenuItemModal({
          ChefService,
          RestService,
          UIService,
          pResponseMenuItem,
          EditMenuItem,
          pStateToReduxMenuItem,
        })(pResponseMenuItem(menuItem))
      } else {
        // create copy item children
        if (isCopy) {
          data.childMenuItems &&
            data.childMenuItems.forEach((item) => {
              item.parentMenuItemId = menuItem.id
              SaveMenuItem({
                ChefService,
                RestService,
                UIService,
                pRequestUpdateMarketplaceAddOn,
                pRequestUpdateMenuItem,
                pRequestUpdateItemImages,
                pResponseMenuItem,
                pResponseMenuItems,
                pSerializeLoadMenuItemsParams,
                CloseChildMenuItemModal,
                EditMenuItem,
                HandleError,
              })(item, isCopy)
            })
          UIService.EditMenuItem.hideCopyMenuItem()
          UIService.EditMenuItem.close()
          UIService.Alert.showPopup({
            message: `"${menuItem.name}" copied!`,
            title: 'Item Alert',
          })
          ChefService.clearEditMenuItem()
        } else {
          UIService.EditMenuItem.close()
          UIService.Alert.showPopup({
            message: `"${menuItem.name}" ${data.id ? 'updated' : 'created'}!`,
            title: 'Item Alert',
          })
          ChefService.clearEditMenuItem()
        }
      }

      if (needResponsePresenter) {
        return pResponseMenuItem(menuItem)
      } else {
        return true
      }
    } catch (error) {
      HandleError({ error, namespace: 'menuItemModal' })

      return false
    }
  }

export const DeleteMenuItem =
  ({ ChefService, RestService, UIService, HandleError }) =>
  async ({ id, name }) => {
    const doDelete = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to archive menu item "${name}"?`,
    })
    if (doDelete) {
      try {
        await RestService.delete(`/api/admin/menu_items/${id}`)
        ChefService.clearEditMenuItem()
        UIService.EditMenuItem.close()
      } catch (error) {
        HandleError({ error })
      }
    }
  }

export const DeleteChildMenuItem =
  ({ RestService, UIService, HandleError }) =>
  async ({ id, name }) => {
    const doDelete = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to archive menu item "${name}"?`,
    })
    if (doDelete) {
      try {
        await RestService.delete(`/api/admin/menu_items/${id}`)

        return true
      } catch (error) {
        HandleError({ error })

        return false
      }
    }
  }

export const DeleteMenuItems =
  ({ UIService, RestService }) =>
  async ({ checkedMenuItems }) => {
    const size = checkedMenuItems.length
    const doDelete = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to archive ${size} menu items?`,
    })
    if (doDelete) {
      const failedArchiveItems = []
      const successfulArchivedItemsMap = {}
      for (let i = 0; i < size; i++) {
        const menuItem = checkedMenuItems[i]
        try {
          const success = await RestService.delete(
            `/api/admin/menu_items/${menuItem.id}`,
          )
          if (success) {
            successfulArchivedItemsMap[menuItem.id] = menuItem.name
          }
        } catch (error) {
          failedArchiveItems.push(menuItem.name)
        }
      }

      if (failedArchiveItems.length > 0) {
        UIService.FlashMessage.displayFailureMessage(
          `Failed to archive ${
            failedArchiveItems.length
          } menu items.\n ${failedArchiveItems.join(',\n')}`,
        )
      }
      if (Object.keys(successfulArchivedItemsMap).length > 0) {
        UIService.FlashMessage.displaySuccessMessage(
          `Successfully archived ${
            Object.keys(successfulArchivedItemsMap).length
          } menu items \n ${Object.values(successfulArchivedItemsMap).join(
            ',\n',
          )}`,
        )
      }

      return successfulArchivedItemsMap
    }

    return []
  }

export const RestoreMenuItem =
  ({ ChefService, RestService, UIService, HandleError }) =>
  async ({ id, name }) => {
    const doRestore = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to restore menu item "${name}"?`,
    })
    if (doRestore) {
      try {
        await RestService.put(`/api/admin/menu_items/restore`, { id })
        ChefService.clearEditMenuItem()
        UIService.EditMenuItem.close()
      } catch (error) {
        HandleError({ error })
      }
    }
  }

export const BulkRestoreMenuItems =
  ({ UIService, RestService, HandleError }) =>
  async ({ checkedMenuItems }) => {
    const size = checkedMenuItems.length
    const doRestore = await UIService.ConfirmationModal.show({
      text: `Are you sure you want to restore ${size} menu items?`,
    })
    if (doRestore) {
      for (let i = 0; i < size; i++) {
        const menuItem = checkedMenuItems[i]
        try {
          await RestService.put(`/api/admin/menu_items/restore`, {
            id: menuItem.id,
          })
        } catch (error) {
          UIService.Alert.showPopup({
            message: `Failed to restore ${menuItem.name}`,
            title: 'Failure',
          })
          HandleError({ error })

          return false
        }
      }

      UIService.Alert.showPopup({
        message: `Restored ${size} menu items`,
        title: 'Success',
      })

      return true
    }

    return false
  }

export const CopyMenuItems =
  ({ RestService, HandleError }) =>
  async (req) => {
    req = snakeCaseify(req)
    try {
      const messages = await RestService.post(
        '/api/admin/menu_items/copy_from_chef',
        req,
        { timeout: 60000 },
      )

      return messages
    } catch (error) {
      HandleError({ error })

      return undefined
    }
  }

export const UpdateMenuItemHasMods =
  ({ RestService, HandleError }) =>
  async (hasMods, menuItemId) => {
    try {
      await RestService.put(`/api/admin/menu_items/${menuItemId}`, {
        menu_item: { has_mods: hasMods },
      })

      return true
    } catch (error) {
      HandleError({ error })

      return false
    }
  }
